<template>
  <teleport to="body">
    <transition name="ai-panel">
      <div v-if="isOpen" class="ai-chat-panel b-1" role="complementary" aria-label="AI Chat">
        <!-- Header -->
        <div class="ai-chat-header flex items-center g-3 bb-1">
          <font-awesome-icon icon="robot" class="text-primary" />
          <div class="grow">
            <span class="font-semibold">AI Chat</span>
            <span v-if="currentRecord" class="text-7 text-muted ml-2"
              >Viewing: {{ currentRecord.name }}</span
            >
          </div>
          <button class="ai-header-btn r-2" title="Clear conversation" @click="clear">
            <font-awesome-icon icon="trash-alt" />
          </button>
          <button class="ai-header-btn r-2" title="Close" @click="close">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <!-- Messages -->
        <div ref="messagesContainer" class="ai-chat-messages" @click="handleLinkClick">
          <div v-if="messages.length === 0" class="ai-chat-empty text-muted text-center">
            <font-awesome-icon icon="robot" class="text-1 mb-3" style="opacity: 0.3" />
            <p v-if="currentRecord">
              Ask a question about {{ currentRecord?.name }} or anything else.
            </p>
            <p v-else>Ask a question about anything.</p>
          </div>

          <div v-for="(msg, i) in messages" :key="i" class="ai-chat-bubble" :class="msg.role">
            <div class="bubble-content" v-html="renderContent(msg)"></div>
            <div v-if="msg.role === 'assistant' && msg.content" class="bubble-actions">
              <button
                class="copy-btn"
                :title="savingDocIndex === i ? 'Saving...' : 'Save as document'"
                :disabled="savingDocument"
                @click="saveAsDocument(msg.content, i)"
              >
                <font-awesome-icon
                  :icon="savingDocIndex === i ? 'spinner' : 'file-alt'"
                  :spin="savingDocIndex === i"
                />
              </button>
              <button class="copy-btn" title="Copy to clipboard" @click="copyMessage(msg.content)">
                <font-awesome-icon :icon="copiedIndex === i ? 'check' : 'copy'" />
              </button>
            </div>
          </div>

          <!-- Streaming indicator -->
          <div v-if="isStreaming" class="ai-streaming-indicator">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="ai-chat-error flex items-center g-2 bb-1">
          <font-awesome-icon icon="exclamation-triangle" class="text-danger" />
          <span class="text-7 grow">
            {{ errorText }}
            <router-link v-if="isApiKeyError" to="/settings">Add it in Settings.</router-link>
          </span>
          <button class="btn btn-sm btn-danger btn-outlined" @click="retry">Retry</button>
        </div>

        <!-- Input -->
        <div class="ai-chat-input bb-1">
          <textarea
            ref="inputEl"
            v-model="input"
            placeholder="Ask about this record..."
            aria-label="Chat message"
            rows="1"
            @keydown="handleKeydown"
          ></textarea>
          <button
            class="ai-send-btn r-2"
            :disabled="!input.trim() || isStreaming"
            @click="handleSend"
          >
            <font-awesome-icon :icon="isStreaming ? 'stop' : 'paper-plane'" />
          </button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import DOMPurify from "dompurify"
import { marked } from "marked"
import { getRecordUrl } from "@/common/records"
import { useChat } from "@/composables/useChat"
import { client } from "@/init/apollo"

const {
  messages,
  isOpen,
  isStreaming,
  error,
  currentRecord,
  pendingNavigate,
  savingDocument,
  send,
  clear,
  close,
  retry,
  setRecord,
  clearRecord,
  saveMessageAsDocument,
} = useChat({ client })

const route = useRoute()
const router = useRouter()
const input = ref("")
const inputEl = ref<HTMLTextAreaElement>()
const messagesContainer = ref<HTMLElement>()
const copiedIndex = ref<number | null>(null)
const savingDocIndex = ref<number | null>(null)

function saveAsDocument(content: string, msgIndex: number) {
  if (savingDocIndex.value !== null) return
  savingDocIndex.value = msgIndex
  saveMessageAsDocument(content)
    .then((doc) => {
      router.push(getRecordUrl(doc))
      close()
    })
    .catch((err) => {
      console.error("Failed to save document:", err)
      error.value = "Failed to save document"
    })
    .finally(() => {
      savingDocIndex.value = null
    })
}

const isApiKeyError = computed(() => error.value?.includes("API key not configured") ?? false)
const errorText = computed(() => {
  if (!error.value) return ""
  if (isApiKeyError.value) return "Anthropic API key not configured."
  return error.value
})

function copyMessage(content: string) {
  navigator.clipboard.writeText(content).then(() => {
    const idx = messages.value.findIndex((m) => m.content === content)
    copiedIndex.value = idx
    setTimeout(() => {
      copiedIndex.value = null
    }, 1500)
  })
}

// Render message content
function renderContent(msg: { role: string; content: string }): string {
  if (msg.role === "user") {
    const div = document.createElement("div")
    div.textContent = msg.content
    return div.innerHTML
  }
  return DOMPurify.sanitize(marked.parse(msg.content, { async: false }) as string)
}

// Auto-scroll to bottom when messages change
function scrollToBottom() {
  nextTick(() => {
    const el = messagesContainer.value
    if (el) el.scrollTop = el.scrollHeight
  })
}

watch(
  () => messages.value[messages.value.length - 1]?.content,
  () => scrollToBottom(),
  { flush: "post" }
)

watch(
  () => messages.value.length,
  () => scrollToBottom(),
  { flush: "post" }
)

// Focus input when panel opens
watch(isOpen, (open) => {
  if (open) {
    nextTick(() => inputEl.value?.focus())
  }
})

// Tell the composable which record the user is looking at
watch(
  () => ({ name: route.name, params: { ...route.params } }),
  (r) => {
    if (r.name === "record-show" && r.params.slug && r.params.recordType) {
      setRecord(r.params.slug as string, r.params.recordType as string)
    } else {
      clearRecord()
    }
  },
  { immediate: true }
)

// Link click interception for SPA navigation
const ALLOWED_PREFIXES = [
  "/r/",
  "/list/",
  "/tags/",
  "/members",
  "/events",
  "/pipelines",
  "/funds",
  "/calendar",
  "/search",
  "/home",
]

function handleLinkClick(e: MouseEvent) {
  const anchor = (e.target as HTMLElement).closest("a")
  if (!anchor) return
  const href = anchor.getAttribute("href")
  if (!href || !href.startsWith("/") || !ALLOWED_PREFIXES.some((p) => href.startsWith(p))) return
  e.preventDefault()
  router.push(href)
}

// Navigate action from AI
watch(pendingNavigate, (action) => {
  if (action) {
    pendingNavigate.value = null
    router.push(action.url)
  }
})

// Input handling
function handleSend() {
  if (!input.value.trim() || isStreaming.value) return
  const msg = input.value
  input.value = ""
  send(msg)
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault()
    handleSend()
  }
}

// Escape to close (only when command palette is not open)
function handleEscape(e: KeyboardEvent) {
  if (e.key === "Escape" && isOpen.value) {
    // Don't close if a dialog/overlay is open (command palette uses z-index 9999)
    const overlay = document.querySelector(".command-palette-overlay")
    if (!overlay) close()
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscape)
})
</script>

<style>
.ai-chat-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 380px;
  background: var(--auger-bg);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);

  .ai-chat-header {
    padding: 0.75rem 1rem;
    flex-shrink: 0;
  }

  .ai-header-btn {
    background: none;
    border: none;
    color: var(--auger-text-weak);
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    transition: all 150ms;

    &:hover {
      background: var(--auger-bg);
      color: var(--auger-text);
    }
  }

  .ai-chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .ai-chat-empty {
    margin: auto;
    padding: 2rem;
  }

  .ai-chat-bubble {
    max-width: 90%;
    padding: 0.6rem 0.85rem;
    border-radius: 12px;
    font-size: 0.9rem;
    line-height: 1.5;
    position: relative;

    .bubble-actions {
      position: absolute;
      top: 0.3rem;
      right: 0.3rem;
      display: flex;
      gap: 0.15rem;
      opacity: 0;
      transition: opacity 150ms;
    }

    .copy-btn {
      background: none;
      border: none;
      color: var(--auger-text-weak);
      cursor: pointer;
      padding: 0.15rem 0.3rem;
      font-size: 0.75rem;
      border-radius: 4px;

      &:hover {
        background: var(--auger-bg);
        color: var(--auger-text);
      }

      &:disabled {
        opacity: 0.4;
        cursor: default;
      }
    }

    &:hover .bubble-actions {
      opacity: 1;
    }

    &.user {
      align-self: flex-end;
      background: var(--auger-primary);
      color: white;
      border-bottom-right-radius: 4px;
    }

    &.assistant {
      align-self: flex-start;
      background: var(--auger-bg);
      border-bottom-left-radius: 4px;

      .bubble-content {
        h1 {
          font-size: 1.1rem;
        }
        h2 {
          font-size: 1rem;
        }
        h3 {
          font-size: 0.95rem;
        }
        h1,
        h2,
        h3 {
          margin: 0.75rem 0 0.25rem;
          font-weight: 600;
        }
        h1:first-child,
        h2:first-child,
        h3:first-child {
          margin-top: 0;
        }

        p {
          margin: 0.4rem 0;
        }
        p:first-child {
          margin-top: 0;
        }
        p:last-child {
          margin-bottom: 0;
        }

        ul {
          margin: 0.4rem 0;
          padding-left: 1.5rem;
          list-style: disc;
        }
        ol {
          margin: 0.4rem 0;
          padding-left: 1.5rem;
        }
        li {
          margin: 0.15rem 0;
        }

        a {
          color: var(--auger-link);
          text-decoration: underline;
        }

        table {
          border-collapse: collapse;
          margin: 0.5rem 0;
          font-size: 0.85em;
          width: 100%;
        }
        th,
        td {
          border: 1px solid var(--auger-border);
          padding: 0.25rem 0.5rem;
          text-align: left;
        }
        th {
          background: var(--auger-bg);
          font-weight: 600;
        }

        pre {
          background: var(--auger-bg);
          padding: 0.5rem;
          border-radius: 6px;
          overflow-x: auto;
          margin: 0.5rem 0;
          font-size: 0.8rem;
        }

        code {
          font-family: var(--auger-font-mono);
          font-size: 0.85em;
        }
      }
    }
  }

  .ai-streaming-indicator {
    display: flex;
    gap: 4px;
    padding: 0.5rem 0.85rem;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--auger-text-weak);
      animation: ai-dot-pulse 1.4s infinite ease-in-out;

      &:nth-child(2) {
        animation-delay: 0.2s;
      }
      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }

  .ai-chat-error {
    padding: 0.5rem 1rem;
    background: var(--auger-danger-light);
    flex-shrink: 0;
  }

  .ai-chat-input {
    padding: 0.75rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-shrink: 0;

    textarea {
      flex: 1;
      border: 1px solid var(--auger-border);
      border-radius: 8px;
      padding: 0.5rem 0.75rem;
      font-family: inherit;
      font-size: 0.9rem;
      resize: none;
      background: transparent;
      color: var(--auger-text);
      max-height: 120px;
      outline: none;

      &:focus {
        border-color: var(--auger-primary);
      }
    }
  }

  .ai-send-btn {
    background: var(--auger-primary);
    color: white;
    border: none;
    padding: 0.4rem 0.6rem;
    cursor: pointer;
    transition: opacity 150ms;

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }

    &:hover:not(:disabled) {
      opacity: 0.85;
    }
  }
}

/* Slide-in transition */
.ai-panel-enter-active,
.ai-panel-leave-active {
  transition: transform 0.25s ease;
}
.ai-panel-enter-from,
.ai-panel-leave-to {
  transform: translateX(100%);
}

/* Push main content when panel is open */
#app.ai-panel-open {
  margin-right: 380px;
  transition: margin-right 0.25s ease;
}

@keyframes ai-dot-pulse {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
